import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import { Navigate } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useUserInfoContext } from "../../global/hooks/useUserInfoContext";
import { IDirectoryContextType } from "../types/interfaces/IDirectoryContextType";
import { IDirectoryProviderProps } from "../types/interfaces/IDirectoryProviderProps";
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { IFilters } from "../types/interfaces/IFilters";
import useGetFilters from '../hooks/useGetFilters';
import { IFiltersResponse } from '../types/interfaces/IFiltersResponse';

// NOTE: The following Co-Pilot suggestions are intentionally not used in this file.
// - Error Message Handling
//      Reason: The error handling is done in the useGetFilters hook and the DirectoryProvider component.
//      Error messaging is done within the ErrorContext and Error component.
// - Consistent Naming (specifically to use same casing for state and Class properties):
//      Reason: The naming convention differences between state objects and class objects are intentional.
//      Class property casing (PascalCase) must align with the API response, while state objects are camelCase for consistency in React.

export const DirectoryContext = createContext<IDirectoryContextType | undefined>(undefined);

/**
 * Custom hook to access the DirectoryContext.
 * Throws an error if used outside of a DirectoryProvider.
 * 
 * @returns {IDirectoryContext} The Directory context value.
 */
export const useDirectoryContext = (): IDirectoryContextType => {
    const context = useContext(DirectoryContext);
    if (!context) {
        throw new Error('useDirectoryContext must be used within a DirectoryProvider');
    }
    return context;
};

/**
 * DirectoryProvider Component
 * 
 * This provider component fetches the initial directory data and provides it to all child components.
 * It also handles the loading state and error state.
 * 
 * Example usage:
 * ```tsx
 * <DirectoryProvider>
 *     <YourComponent />
 * </DirectoryProvider>
 * ```
 * 
 * @param {React.ReactNode} children - The child components that will have access to the DirectoryContext.
 * @returns {JSX.Element} The Directory context provider with the initial Directory data.
 */
export const DirectoryProvider: React.FC<IDirectoryProviderProps> = ({ children }) => {
    const [loadingDirCtx, setLoadingDirCtx] = useState<boolean>(true);
    const [hasAccess] = useState<boolean>(false);
    const { userInfo } = useUserInfoContext();
    const { setError } = useErrorContext(); //this error will display on UI so use wisely!

    const [originalFiltersList, setOriginalFiltersList] = useState<IFilters>();
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [stateOptions, setStateOptions] = useState<string[]>([]);
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [zipOptions, setZipOptions] = useState<string[]>([]);
    const [specialtyOptions, setSpecialtyOptions] = useState<string[]>([]);
    const [interestOptions, setInterestOptions] = useState<string[]>([]);
    const [languageOptions, setLanguageOptions] = useState<string[]>([]);

    const filtersResponse: IFiltersResponse = useGetFilters();
    const filters: IFilters | undefined = filtersResponse.data == null ? undefined : filtersResponse.data;
    const [filtersError, setFiltersError] = useState(filtersResponse.isError);

    useEffect(() => {
        if (!filtersResponse.loading) {
            setOriginalFiltersList(filters);
            setAutocompleteOptions(filters);
            setLoadingDirCtx(false);
        }
        if (filtersResponse.isError) {
            console.log("filtersResponse.isError in DirectoryProvider");
            setFiltersError(true);
            setError({ message: null, statusCode: 0 });
        }
    }, [filtersResponse.loading, filtersResponse.isError]);


    function setAutocompleteOptions(filters: IFilters | null | undefined): void {
        if (!filters) {
            console.log("filters is null/undefined in DirectoryContext.SetAutocompleteOptions");
            setFiltersError(true);
            return;
        }

        const optionSetters = [
            { key: 'CountryFilter', setter: setCountryOptions, unique: true },
            { key: 'StateFilter', setter: setStateOptions, unique: true },
            { key: 'CityFilter', setter: setCityOptions, unique: true },
            { key: 'ZipFilter', setter: setZipOptions, unique: true },
            { key: 'LanguageFilter', setter: setLanguageOptions, unique: false },
            { key: 'SpecialtyFilter', setter: setSpecialtyOptions, unique: false },
            { key: 'InterestFilter', setter: setInterestOptions, unique: false },
        ];

        optionSetters.forEach(({ key, setter, unique }) => {
            const value = (filters as any)[key];
            if (value == null) {
                console.log(`filters.${key} is null/undefined in DirectoryContext.SetAutocompleteOptions`);
            } else {
                setter(unique ? getUniqueSortedList(value) : value);
            }
        });
    }

    function getUniqueSortedList(items: string[]): string[] {
        return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
    }

    const value = useMemo(() => ({
        loadingDirCtx,
        hasAccess: userInfo?.CanSeeMemberDirectory ?? false,
        originalFiltersList,
        countryOptions,
        stateOptions,
        cityOptions,
        zipOptions,
        specialtyOptions,
        interestOptions,
        languageOptions
    }), [
        loadingDirCtx,
        hasAccess,
        originalFiltersList,
        countryOptions,
        stateOptions,
        cityOptions,
        zipOptions,
        specialtyOptions,
        interestOptions,
        languageOptions
    ]);

    if (filtersError) {
        return <Navigate to="/memberdirectory/error" />;
    }

    return (
        <DirectoryContext.Provider value={value}>
            {loadingDirCtx ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                children
            )}
        </DirectoryContext.Provider>
    );
};

